import { onMounted, onUnmounted, ref } from 'vue'
import { useMatchStore } from '@/stores/match.js'
import { bridge } from '@/services/bridge.js'

export function useVisibilityRestore(getMatchId) {
    const matchStore = useMatchStore()
    const isRestoring = ref(false)
    let lastHiddenTime = null

    const STALE_THRESHOLD_MS = 30000 // 30 seconds

    async function checkAndRestore() {
        const matchId = getMatchId()
        if (!matchId) return

        // Persisted match state survives a backgrounded tab even though the
        // realtime channel does not, so a missing/stale channel also needs a
        // resync, not just genuinely empty match data.
        const needsRestore =
            (!matchStore.match?.matchtype_id && !matchStore.setup?.matchtype_id) ||
            !matchStore.activeChannel

        if (needsRestore) {
            console.log('[VisibilityRestore] Match data missing, restoring...')
            isRestoring.value = true
            try {
                await matchStore.restoreMatchFromAPI(matchId)
            } catch (error) {
                console.error('[VisibilityRestore] Failed to restore:', error)
            } finally {
                isRestoring.value = false
            }
        }
    }

    function handleAppResumed() {
        console.log('[VisibilityRestore] Native app.resumed received')
        checkAndRestore()
    }

    function handleVisibilityChange() {
        if (document.visibilityState === 'hidden') {
            lastHiddenTime = Date.now()
        } else if (document.visibilityState === 'visible') {
            const wasHiddenLong = lastHiddenTime && Date.now() - lastHiddenTime > STALE_THRESHOLD_MS
            if (wasHiddenLong) {
                console.log('[VisibilityRestore] Page visible after long hide')
                checkAndRestore()
            }
            lastHiddenTime = null
        }
    }

    function handlePageShow(event) {
        if (event.persisted) {
            console.log('[VisibilityRestore] Page restored from bfcache')
            checkAndRestore()
        }
    }

    let unsubscribeBridge = null

    onMounted(() => {
        unsubscribeBridge = bridge.on('app.resumed', handleAppResumed)
        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('pageshow', handlePageShow)
    })

    onUnmounted(() => {
        if (unsubscribeBridge) unsubscribeBridge()
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        window.removeEventListener('pageshow', handlePageShow)
    })

    return { isRestoring }
}
